import Translate from "Components/Translate";
import { PHONE, TAXI } from "Constant";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { classNames } from "primereact/utils";
import { Dropdown } from "primereact/dropdown";

// Validation schema using Zod
const passengerFormSchema = z.object({
  firstname: z
    .string()
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s]*$/, "First name should only contain letters and spaces"),
  lastname: z
    .string()
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s]*$/, "Last name should only contain letters and spaces"),
  phone: z
    .string()
    .regex(/^\d{8}$/, "Please enter a valid 8-digit phone number"),
  passengersCount: z
    .number()
    .min(1, "At least 1 passenger is required")
    .max(4, "Maximum 4 passengers allowed"),
  taxi: z
    .string()
    .nullable()
    .refine((val) => val !== null, "Please select a taxi")
});

const PassengerForm = ({
  passengerInfo,
  setPassengerInfo,
  inputs,
  setInputs,
  onValidSubmit,
  options,
  isLoading,
  onChangeTaxi
}) => {
  const methods = useForm({
    resolver: zodResolver(passengerFormSchema),
    defaultValues: {
      firstname: passengerInfo?.firstname || "",
      lastname: passengerInfo?.lastname || "",
      phone: passengerInfo?.phone || "",
      passengersCount: inputs?.passengersCount || 1,
      taxi: inputs?.taxi || null
    }
  });

  const {
    register,
    formState: { errors },
    setValue,
    trigger,
    handleSubmit
  } = methods;

  // Expose validation methods to parent
  React.useEffect(() => {
    if (onValidSubmit) {
      onValidSubmit(methods);
    }
  }, [onValidSubmit]);

  const onChange = async (name, val) => {
    // For phone, remove any non-digit characters before validation
    const processedVal = name === 'phone' ? val.replace(/\D/g, '') : val;
    setValue(name, processedVal);
    await trigger(name);
    setPassengerInfo((prevState) => ({ ...prevState, [name]: processedVal }));
  };

  const onChangeCount = async (val) => {
    setValue("passengersCount", val);
    await trigger("passengersCount");
    setInputs((prevState) => ({ ...prevState, passengersCount: val }));
  };

  const handleTaxiChange = async (val) => {
    setValue("taxi", val);
    await trigger("taxi");
    if (onChangeTaxi) onChangeTaxi(val);
  };

  return (
    <div className="grid p-fluid field col-12">
      <div className="field col-12 md:col-3">
        <label className={classNames({ "p-error": errors.firstname })}>
          <Translate>Firstname</Translate>*
        </label>
        <InputText
          {...register("firstname")}
          value={passengerInfo?.firstname || ""}
          className={classNames({ "p-invalid": errors.firstname })}
          onChange={(e) => {
            onChange("firstname", e?.target?.value);
          }}
        />
        {errors.firstname && (
          <small className="p-error">{errors.firstname.message}</small>
        )}
      </div>
      <div className="field col-12 md:col-3">
        <label className={classNames({ "p-error": errors.lastname })}>
          <Translate>Lastname</Translate>*
        </label>
        <InputText
          {...register("lastname")}
          value={passengerInfo?.lastname || ""}
          className={classNames({ "p-invalid": errors.lastname })}
          onChange={(e) => {
            onChange("lastname", e?.target?.value);
          }}
        />
        {errors.lastname && (
          <small className="p-error">{errors.lastname.message}</small>
        )}
      </div>
      <div className="field col-12 md:col-3">
        <label className={classNames({ "p-error": errors.phone })}>
          <Translate>{PHONE}</Translate>*
        </label>
        <span className="p-input-icon-left">
          <InputText
            {...register("phone")}
            value={passengerInfo?.phone || ""}
            className={classNames({ "p-invalid": errors.phone })}
            onChange={(e) => {
              onChange("phone", e?.target?.value);
            }}
            maxLength={8}
          />
        </span>
        {errors.phone && (
          <small className="p-error block">{errors.phone.message}</small>
        )}
      </div>
      <div className="field col-12 md:col-3">
        <label className={classNames({ "p-error": errors.passengersCount })}>
          <Translate>Nombre de passagers</Translate>*
        </label>
        <InputNumber
          value={inputs?.passengersCount || 1}
          className={classNames({ "p-invalid": errors.passengersCount })}
          onValueChange={(e) => onChangeCount(e?.value)}
          min={1}
          max={4}
          showButtons
        />
        {errors.passengersCount && (
          <small className="p-error">{errors.passengersCount.message}</small>
        )}
      </div>
      <div className="field col-12 md:col-3">
        <label className={classNames({ "p-error": errors.taxi })}>
          <Translate>{TAXI}</Translate>*
        </label>
        <Dropdown
          loading={isLoading}
          filter
          value={inputs?.taxi || null}
          onChange={(e) => handleTaxiChange(e?.value)}
          options={options}
          optionLabel="label"
          className={classNames({ "p-invalid": errors.taxi })}
          showClear={true}
        />
        {errors.taxi && (
          <small className="p-error">{errors.taxi.message}</small>
        )}
      </div>
    </div>
  );
};

export default PassengerForm;
